import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SingleParentFormComponent } from './single-parent-form.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Address, ParentInfo, School } from '@awo-care/shared-core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'lib-parent-info-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    SingleParentFormComponent,
  ],
  template: `
    <div class="parent-form">
      @if (!readOnly() && !editMode()) {
      <div class="step-description">
        <span i18n="@@parent-info-form.description"
          >Bitte geben Sie die Kontaktdaten der Erziehungsberechtigten an.</span
        >
      </div>
      }

      <form [formGroup]="form()" class="form-content" (focusout)="onFormBlur()">
        <div class="section-header">
          <h3 i18n="@@parent-info-form.guardians">Erziehungsberechtigte</h3>
          @if (canEdit() && readOnly() && !editMode()) {
          <button
            mat-button
            type="button"
            (click)="toggleEditMode()"
            class="edit-button"
          >
            <mat-icon>edit</mat-icon>
            <span i18n="@@parent-info-form.edit">Bearbeiten</span>
          </button>
          } @if (editMode()) {
          <div class="edit-actions">
            <button mat-button type="button" (click)="cancelEdit()">
              <span i18n="@@parent-info-form.cancel">Abbrechen</span>
            </button>
            <button
              mat-raised-button
              color="primary"
              type="button"
              (click)="saveChanges()"
              [disabled]="!form().valid || !form().dirty"
            >
              <span i18n="@@parent-info-form.save">Speichern</span>
            </button>
          </div>
          }
        </div>

        <div class="radio-group" data-testid="single-parenting">
          <div class="radio-label">
            <span i18n="@@parent-info-form.singleParentingLabel"
              >Alleinerziehend und nicht in einer eheähnlichen Gemeinschaft
              lebend</span
            >
          </div>
          <mat-radio-group
            formControlName="singleParenting"
            class="radio-buttons"
          >
            <mat-radio-button [value]="true"
              ><span i18n="@@parent-info-form.yes">Ja</span></mat-radio-button
            >
            <mat-radio-button [value]="false"
              ><span i18n="@@parent-info-form.no">Nein</span></mat-radio-button
            >
          </mat-radio-group>
        </div>

        @if (!form().get('singleParenting')?.value) {
        <div class="dual-parent-forms">
          <div class="parent-section">
            <h4 i18n="@@parent-info-form.parent1">Elternteil 1</h4>
            <lib-single-parent-form
              [parentForm]="getParentGroup('parent1')"
              [parentType]="'parent1'"
              [useGoogleMapsAutocomplete]="useGoogleMapsAutocomplete()"
              [readOnly]="readOnly()"
              [editMode]="editMode()"
            />
          </div>
          <div class="parent-section">
            <h4 i18n="@@parent-info-form.parent2">Elternteil 2</h4>
            <lib-single-parent-form
              [parentForm]="getParentGroup('parent2')"
              [parentType]="'parent2'"
              [useGoogleMapsAutocomplete]="useGoogleMapsAutocomplete()"
              [readOnly]="readOnly()"
              [editMode]="editMode()"
            />
          </div>
        </div>
        } @else {
        <lib-single-parent-form
          [parentForm]="getParentGroup('parent1')"
          [parentType]="'parent1'"
          [useGoogleMapsAutocomplete]="useGoogleMapsAutocomplete()"
          [readOnly]="readOnly()"
          [editMode]="editMode()"
        />
        }

        <mat-checkbox formControlName="hasSibling" class="siblingCheckbox">
          <span i18n="@@parent-info-form.siblingCheckbox"
            >Wir haben bereits ein Kind in der AWO-Schülerbetreuung</span
          >
        </mat-checkbox>

        @if (form().get('hasSibling')?.value) {
        <div class="sibling-fields">
          <mat-form-field appearance="outline">
            <mat-label i18n="@@parent-info-form.siblingName"
              >Name des Geschwisterkindes</mat-label
            >
            <input matInput formControlName="siblingName" />
            @if (form().get('siblingName')?.hasError('required') &&
            form().get('siblingName')?.touched) {
            <mat-error i18n="@@parent-info-form.siblingNameRequired"
              >Name des Geschwisterkindes ist erforderlich</mat-error
            >
            }
          </mat-form-field>

          <div class="school-fields">
            @if (currentSchool()) {
            <mat-form-field appearance="outline">
              <mat-label i18n="@@parent-info-form.siblingSchool"
                >Schule des Geschwisterkindes</mat-label
              >
              <mat-select formControlName="siblingUseCurrentSchool">
                <mat-option [value]="'same'">
                  <span i18n="@@parent-info-form.sameSchool"
                    >Gleiche Schule</span
                  >
                  ({{ currentSchool()!.schoolName }})
                </mat-option>
                <mat-option [value]="'other-awo'">
                  <span i18n="@@parent-info-form.otherAwoSchool"
                    >Andere AWO Schule</span
                  >
                </mat-option>
                <mat-option [value]="'other'">
                  <span i18n="@@parent-info-form.otherSchool"
                    >Andere Schule</span
                  >
                </mat-option>
              </mat-select>
            </mat-form-field>
            } @if (form().get('siblingUseCurrentSchool')?.value === 'other-awo'
            || form().get('siblingUseCurrentSchool')?.value === 'other' ||
            !currentSchool()) {
            <mat-form-field appearance="outline">
              <mat-label i18n="@@parent-info-form.schoolName"
                >Name der Schule</mat-label
              >
              <input matInput formControlName="siblingSchoolName" />
              @if (form().get('siblingSchoolName')?.hasError('required') &&
              form().get('siblingSchoolName')?.touched) {
              <mat-error i18n="@@parent-info-form.schoolNameRequired"
                >Name der Schule ist erforderlich</mat-error
              >
              }
            </mat-form-field>
            }
          </div>
        </div>
        }
      </form>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .step-description {
        color: #666;
        line-height: 1.5;
        margin-bottom: 2rem;
        font-size: 0.95rem;
        background-color: #f5f5f5;
        padding: 1rem;
        border-radius: 4px;
        border-left: 4px solid var(--theme-accent);
      }

      .form-content {
        display: grid;
        gap: 2rem;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .section-header h3 {
        margin: 0;
        color: var(--theme-regularTextColor);
        font-size: 1.1rem;
        font-weight: 500;
      }

      .section {
        display: grid;
        gap: 1rem;
      }

      .section h3 {
        margin: 0 0 1rem;
        color: rgba(0, 0, 0, 0.87);
        font-size: 1.2rem;
        font-weight: 500;
      }

      .parent-fields {
        display: grid;
        gap: 1rem;
      }

      @media (min-width: 768px) {
        .parent-fields {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      mat-form-field {
        width: 100%;
      }

      .form-group {
        display: grid;
        gap: 1rem;
      }

      .radio-group {
        display: grid;
        gap: 0.5rem;
        grid-column: 1 / -1;
      }

      .label {
        font-size: 1rem;
        color: rgba(0, 0, 0, 0.87);
      }

      .radio-buttons {
        display: flex;
        gap: 1rem;
      }

      .info-note {
        color: #666;
        font-size: 0.875rem;
        line-height: 1.5;
        margin-top: 1rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: 4px;
        grid-column: 1 / -1;

        p {
          margin: 0;
        }
      }

      .sibling-fields {
        display: grid;
        gap: 1rem;
        margin-top: -1rem;
      }

      @media (min-width: 768px) {
        .sibling-fields {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .edit-button {
        color: var(--theme-accent);
      }

      .edit-actions {
        display: flex;
        gap: 1rem;
      }

      .dual-parent-forms {
        display: grid;
        gap: 2rem;
        margin-top: 1rem;
      }

      .parent-section h4 {
        margin: 0 0 1rem 0;
        color: var(--theme-regularTextColor, rgba(0, 0, 0, 0.87));
        font-size: 1.1rem;
        font-weight: 500;
      }

      .sibling-fields {
        display: grid;
        gap: 1rem;
      }

      @media (min-width: 768px) {
        .sibling-fields {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .school-fields {
        display: grid;
        gap: 1rem;
        grid-column: 1 / -1;
      }

      @media (min-width: 768px) {
        .school-fields {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    `,
  ],
})
export class ParentInfoFormComponent {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  private decimalValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
      if (!control.value && control.value !== 0) {
        return null; // Don't validate empty values
      }

      // Handle both string and numeric values
      let value = control.value.toString();

      // Check if it's a valid number with max 1 decimal place
      const num = parseFloat(value);
      if (isNaN(num)) {
        return { invalidDecimal: { value: control.value } };
      }

      // Check decimal places by converting to string and looking for decimals
      const decimalPlaces = value.includes('.')
        ? value.split('.')[1]?.length || 0
        : 0;

      if (decimalPlaces > 1) {
        return { invalidDecimal: { value: control.value } };
      }

      return null;
    };
  }

  data = input<ParentInfo | null>(null);
  readOnly = input<boolean>(false);
  /**
   * Blendet die Schaltflaeche „Bearbeiten" aus. Default `true` — nur Aufrufer,
   * die Schreibrechte pruefen, setzen das auf `false` (Teamleitung, #875).
   * Ersetzt keine Serverpruefung, die steht in `firestore.rules`.
   */
  canEdit = input<boolean>(true);
  validateTrigger = input(-1);
  stepIndex = input(-1);
  useGoogleMapsAutocomplete = input<boolean>(false);
  currentSchool = input<School | null>(null);
  formControl = output<AbstractControl>();
  formSubmit = output<Partial<ParentInfo>>();

  // Track if we're waiting for an async save to complete
  private pendingSave = signal(false);

  editMode = input<boolean>(false);
  editModeChange = output<boolean>();

  private originalFormValue: any = null;

  private createParentGroup(isParent2 = false) {
    const baseGroup = this.fb.group({
      firstName: [null as string | null, [Validators.required]],
      lastName: [null as string | null, [Validators.required]],
      // Optional: wird aktuell nur fuer Elternteil 2 im Formular gerendert (#913)
      email: [null as string | null, [Validators.email]],
      phoneMobile: [null as string | null, [Validators.required]],
      employed: [null as boolean | null, [Validators.required]],
      occupation: [''],
      weeklyWorkHours: [null as number | null], // Removed initial validators
      legalGuardian: [null as boolean | null, [Validators.required]],
    });

    // Add employed value changes subscription
    baseGroup
      .get('employed')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isEmployed) => {
        const weeklyWorkHoursControl = baseGroup.get('weeklyWorkHours');
        if (isEmployed) {
          weeklyWorkHoursControl?.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(40),
            this.decimalValidator(),
          ]);
        } else {
          weeklyWorkHoursControl?.clearValidators();
          weeklyWorkHoursControl?.setValue(null);
        }
        weeklyWorkHoursControl?.updateValueAndValidity();
      });

    if (!isParent2) {
      return this.fb.group({
        ...baseGroup.controls,
        address: [null as Address | null, [Validators.required]],
        alternativeAddress: [''],
      });
    }

    return this.fb.group({
      ...baseGroup.controls,
      alternativeAddress: [''],
    });
  }

  form = signal(
    this.fb.group({
      parent1: this.createParentGroup(false),
      parent2: this.createParentGroup(true),
      singleParenting: [false as boolean | null, [Validators.required]],
      hasSibling: [false],
      siblingName: [null as string | null],
      siblingUseCurrentSchool: [null as 'same' | 'other-awo' | 'other' | null],
      siblingSchoolName: [null as string | null],
    })
  );

  constructor() {
    // Trigger validation display only when this step is targeted
    effect(() => {
      const trigger = this.validateTrigger();
      if (trigger >= 0 && trigger === this.stepIndex()) {
        this.form().markAllAsTouched();
      }
    });

    // Handle form disable/enable state
    effect(() => {
      const shouldDisable = this.readOnly() && !this.editMode();

      if (shouldDisable) {
        this.form().disable({ emitEvent: false });
      } else {
        this.form().enable({ emitEvent: false });
        // Re-disable parent2 after enabling all controls,
        // otherwise parent2 gets re-enabled even when singleParenting is true
        if (this.form().get('singleParenting')?.value) {
          const parent2Group = this.form().get('parent2') as FormGroup;
          parent2Group.disable({ emitEvent: false });
        }
      }
    });

    effect(() => {
      const parentInfo = this.data();
      if (parentInfo) {
        this.form().patchValue({
          parent1: {
            firstName: parentInfo.parent1?.firstName ?? null,
            lastName: parentInfo.parent1?.lastName ?? null,
            email: parentInfo.parent1?.email ?? null,
            phoneMobile: parentInfo.parent1?.phoneMobile ?? null,
            address: parentInfo.parent1?.address ?? null,
            alternativeAddress: parentInfo.parent1?.alternativeAddress ?? '',
            employed: parentInfo.parent1?.employed ?? null,
            occupation: parentInfo.parent1?.occupation ?? '',
            weeklyWorkHours: parentInfo.parent1?.weeklyWorkHours ?? null,
            legalGuardian: parentInfo.parent1?.legalGuardian ?? null,
          },
          parent2: {
            firstName: parentInfo.parent2?.firstName ?? null,
            lastName: parentInfo.parent2?.lastName ?? null,
            email: parentInfo.parent2?.email ?? null,
            phoneMobile: parentInfo.parent2?.phoneMobile ?? null,
            alternativeAddress: parentInfo.parent2?.alternativeAddress ?? '',
            employed: parentInfo.parent2?.employed ?? null,
            occupation: parentInfo.parent2?.occupation ?? '',
            weeklyWorkHours: parentInfo.parent2?.weeklyWorkHours ?? null,
            legalGuardian: parentInfo.parent2?.legalGuardian ?? null,
          },
          singleParenting: parentInfo.singleParenting ?? true,
          hasSibling: parentInfo.hasSibling ?? false,
          siblingName: parentInfo.siblingName ?? null,
          siblingUseCurrentSchool: parentInfo.siblingUseCurrentSchool ?? null,
          siblingSchoolName: parentInfo.siblingSchoolName ?? null,
        });

        if (parentInfo.singleParenting) {
          this.handleSingleParenting(true);
        }

        // Store original values for cancel functionality
        this.originalFormValue = this.form().getRawValue();
      }
    });

    // Set up form value changes subscription with debouncing
    this.form()
      .valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Always emit the form, let the stepper component decide validity
        this.formControl.emit(this.form());
      });

    this.form()
      .get('singleParenting')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isSingleParent) => {
        this.handleSingleParenting(isSingleParent);
      });

    this.form()
      .get('hasSibling')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.updateSiblingValidation();
      });

    this.form()
      .get('siblingUseCurrentSchool')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.updateSiblingValidation();
      });
  }

  private updateSiblingValidation(): void {
    const hasSibling = this.form().get('hasSibling')?.value;
    const useCurrentSchool = this.form().get('siblingUseCurrentSchool')?.value;
    const siblingNameControl = this.form().get('siblingName');
    const siblingSchoolControl = this.form().get('siblingSchoolName');
    const useCurrentSchoolControl = this.form().get('siblingUseCurrentSchool');

    if (hasSibling) {
      // Name is always required if sibling exists
      siblingNameControl?.setValidators(Validators.required);

      // School name validation logic - only require when field is actually visible in template
      if (useCurrentSchool === 'same' && this.currentSchool()) {
        siblingSchoolControl?.clearValidators();
        siblingSchoolControl?.setValue(this.currentSchool()!.schoolName);
      } else if (useCurrentSchool === 'other-awo' || useCurrentSchool === 'other' || !this.currentSchool()) {
        siblingSchoolControl?.setValidators(Validators.required);
      } else {
        // No selection yet (null/undefined) - don't require school name until user picks an option
        siblingSchoolControl?.clearValidators();
      }
    } else {
      // Clear all sibling fields if no sibling
      siblingNameControl?.clearValidators();
      siblingSchoolControl?.clearValidators();
      useCurrentSchoolControl?.clearValidators();
      siblingNameControl?.setValue(null);
      siblingSchoolControl?.setValue(null);
      useCurrentSchoolControl?.setValue(null);
    }

    siblingNameControl?.updateValueAndValidity();
    siblingSchoolControl?.updateValueAndValidity();
    useCurrentSchoolControl?.updateValueAndValidity();

    // Update the entire form's validity and emit the changes
    this.form().updateValueAndValidity();
    this.formControl.emit(this.form());
  }

  private handleSingleParenting(isSingleParent: boolean | null) {
    const parent2Group = this.form().get('parent2') as FormGroup;

    if (isSingleParent) {
      // For single parents, disable and clear parent2 completely
      parent2Group.disable();
      parent2Group.reset();
      Object.keys(parent2Group.controls).forEach((key) => {
        const control = parent2Group.get(key);
        if (control) {
          control.clearValidators();
          control.setValue(null);
          control.setErrors(null);
          control.updateValueAndValidity();
        }
      });
    } else if (isSingleParent === false) {
      // For dual parents, enable parent2 and set required validators
      // Only enable if not in readOnly mode
      if (!this.readOnly() || this.editMode()) {
        parent2Group.enable();
      }

      // Set validators based on field requirements
      const requiredFields = [
        'firstName',
        'lastName',
        'phoneMobile',
        'employed',
        'legalGuardian',
      ];
      const optionalFields = [
        'occupation',
        'weeklyWorkHours',
        'alternativeAddress',
      ];

      requiredFields.forEach((fieldName) => {
        const control = parent2Group.get(fieldName);
        if (control) {
          control.setValidators([Validators.required]);
          control.updateValueAndValidity();
        }
      });

      optionalFields.forEach((fieldName) => {
        const control = parent2Group.get(fieldName);
        if (control) {
          control.clearValidators();
          control.updateValueAndValidity();
        }
      });

      // email ist optional, braucht aber weiterhin die Formatpruefung (#913).
      // Der Alleinerziehend-Zweig oben raeumt alle Validatoren ab, deshalb hier
      // explizit wiederherstellen statt ueber optionalFields (das wuerde ihn loeschen).
      const parent2EmailControl = parent2Group.get('email');
      if (parent2EmailControl) {
        parent2EmailControl.setValidators([Validators.email]);
        parent2EmailControl.updateValueAndValidity();
      }

      // Re-establish the employed field subscription for weeklyWorkHours validation
      const employedControl = parent2Group.get('employed');
      const weeklyWorkHoursControl = parent2Group.get('weeklyWorkHours');

      if (employedControl && weeklyWorkHoursControl) {
        // Check current employed status and set weeklyWorkHours validators accordingly
        const isEmployed = employedControl.value;
        if (isEmployed) {
          weeklyWorkHoursControl.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(40),
            this.decimalValidator(),
          ]);
        } else {
          weeklyWorkHoursControl.clearValidators();
          weeklyWorkHoursControl.setValue(null);
        }
        weeklyWorkHoursControl.updateValueAndValidity();
      }
    }

    // Update parent2 group validity
    parent2Group.updateValueAndValidity();

    // Update the entire form's validity and emit the changes
    this.form().updateValueAndValidity();
    this.formControl.emit(this.form());
  }

  onFormBlur(): void {
    if (!this.form().valid) {
      // Check parent1 group errors
      const parent1Group = this.form().get('parent1') as FormGroup;
      if (parent1Group && !parent1Group.valid) {
        Object.keys(parent1Group.controls).forEach((key) => {
          const control = parent1Group.get(key);
          if (control && !control.valid) {
            // Validation error in parent1 form control
          }
        });
      }

      // Check parent2 group errors if not single parenting
      const isSingleParenting = this.form().get('singleParenting')?.value;

      if (!isSingleParenting) {
        const parent2Group = this.form().get('parent2') as FormGroup;
        if (parent2Group && !parent2Group.valid) {
          Object.keys(parent2Group.controls).forEach((key) => {
            const control = parent2Group.get(key);
            if (control && !control.valid) {
              // Validation error in parent2 form control
            }
          });
        }
      }

      // Check other direct form controls
      const formGroup = this.form() as FormGroup;
      [
        'singleParenting',
        'hasSibling',
        'siblingName',
        'siblingUseCurrentSchool',
        'siblingSchoolName',
      ].forEach((key) => {
        const control = formGroup.get(key);
        if (control && !control.valid) {
          // Validation error in form control
        }
      });
    }

    // Always emit the form regardless of validity
    this.formControl.emit(this.form());
  }

  getParentGroup(parent: 'parent1' | 'parent2'): FormGroup {
    return this.form().get(parent) as FormGroup;
  }

  private getSiblingSchoolName(formValue: any): string | undefined {
    if (!formValue.hasSibling) {
      return undefined;
    }

    if (formValue.siblingUseCurrentSchool === 'same' && this.currentSchool()) {
      return this.currentSchool()!.schoolName;
    }

    return formValue.siblingSchoolName || undefined;
  }

  toggleEditMode(): void {
    this.editModeChange.emit(true);
  }

  cancelEdit(): void {
    if (this.originalFormValue) {
      this.form().patchValue(this.originalFormValue, { emitEvent: false });
      this.form().markAsPristine();
    }
    this.editModeChange.emit(false);
  }

  saveChanges(): void {
    if (this.form().valid && this.form().dirty) {
      const formValue = this.form().getRawValue();

      // Convert and clean up the data for ParentInfo model
      const parentData: Partial<ParentInfo> = {
        singleParenting: formValue.singleParenting ?? false,
        hasSibling: formValue.hasSibling ?? false,
        siblingName: formValue.siblingName || undefined,
        siblingSchoolName: this.getSiblingSchoolName(formValue),
        parent1: {
          firstName: formValue.parent1['firstName'] || '',
          lastName: formValue.parent1['lastName'] || '',
          phoneMobile: formValue.parent1['phoneMobile'] || undefined,
          address: formValue.parent1['address'] || undefined,
          employed: formValue.parent1['employed'] ?? false,
          occupation: formValue.parent1['occupation'] || '',
          weeklyWorkHours: formValue.parent1['weeklyWorkHours'] || undefined,
          legalGuardian: formValue.parent1['legalGuardian'] ?? false,
          alternativeAddress: formValue.parent1['alternativeAddress'] || '',
        },
      };

      // Only include parent2 if not single parenting
      if (!formValue.singleParenting && formValue.parent2) {
        parentData.parent2 = {
          firstName: formValue.parent2['firstName'] || '',
          lastName: formValue.parent2['lastName'] || '',
          email: formValue.parent2['email'] || undefined,
          phoneMobile: formValue.parent2['phoneMobile'] || undefined,
          employed: formValue.parent2['employed'] ?? false,
          occupation: formValue.parent2['occupation'] || '',
          weeklyWorkHours: formValue.parent2['weeklyWorkHours'] || undefined,
          legalGuardian: formValue.parent2['legalGuardian'] ?? false,
          alternativeAddress: formValue.parent2['alternativeAddress'] || '',
        };
      }

      // Mark that we're waiting for async save
      this.pendingSave.set(true);
      this.formSubmit.emit(parentData);

      // Don't exit edit mode immediately - wait for parent to confirm save
      // this.editModeChange.emit(false); // Commented out for async saves
    }
  }

  // Method for parent to call when save is complete
  confirmSaveComplete(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form().getRawValue();
      this.form().markAsPristine();
      this.pendingSave.set(false);
      this.editModeChange.emit(false);
    }
  }

  // Method to reset form state without closing edit mode (for modal behavior)
  resetFormState(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form().getRawValue();
      this.form().markAsPristine();
      this.pendingSave.set(false);
      // Don't emit editModeChange - keep modal open
    }
  }

  // Method to mark form as saved without closing edit mode
  markAsSaved(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form().getRawValue();
      this.form().markAsPristine();
      this.pendingSave.set(false);
      // Stay in edit mode - don't emit editModeChange
    }
  }
}
